import { FC, useState, useEffect, CSSProperties } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { Route, Routes } from "react-router-dom";
import {
  AuthContext,
  getAuthInfoFromLocalStorage,
  IAuthInfo,
  launchMspPortal,
  clearSession,
  storeSession,
} from "./context/AuthContext";
import { fetchAuthInfoAsync, gotoOAuthPortal } from "./services/auth";
import Header from "./components/Header";
import Home from "./pages/Home";
import Plans from "./pages/Plans";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import Account from "./pages/Account";
import SpecifyDomain from "./pages/SpecifyDomain";
import Crystal from "./pages/Crystal";
import CrystalSuccess from "./pages/CrystalSuccess";
import CrystalLicenses from "./pages/CrystalLicenses";
import Footer from "./components/Footer";
import {
  IInstanceWithRole,
  IMspInstance,
  IMspInstanceDetails,
  InstanceContext,
  getCurrentInstance,
} from "./context/InstanceContext";
import { ISelectedPlanBeforeSignin, LS_KEY, SS_KEY } from "./constants";
import {
  fetchMspInstanceDetailsAsync,
  fetchMspInstancesAsync,
  fetchInstanceList,
} from "./services/instance";
import { Ring } from "@uiball/loaders";
import { fetchUIConfig } from "./services/uiconfig";
import { IUIConfig } from "./data/interface";
import fakeUiConfig from "./data/ui-config.json";
import { UIConfigContext } from "./context/UIConfigContext";
import { checkoutAsync } from "./services/checkout";
import { getEnv, Environment } from "./utils";

const App: FC = () => {
  const { pathname } = useLocation();

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const code = searchParams.get("code");
  // set by the portal SPA: the root owns Cognito sign-out
  const logoutRequested = searchParams.get("logout") === "1";

  /* state */

  const [uiConfig, setUIConfig] = useState<IUIConfig | null>(null);

  const [authInfo, setAuthInfo] = useState<Partial<IAuthInfo>>(() => {
    return getAuthInfoFromLocalStorage() || {};
  });

  const [instances, setInstances] = useState<IMspInstance[]>([]);
  const [instancesWithRole, setInstancesWithRole] = useState<IInstanceWithRole[]>([]);

  const [currentInstanceDetails, setCurrentInstanceDetails] =
    useState<IMspInstanceDetails | null>(null);

  const [freshLogin, setFreshLogin] = useState(false);

  /* loading state */

  const [loadingUIConfig, setLoadingUIConfig] = useState(true);

  const [loadingAuthInfo, setLoadingAuthInfo] = useState(Boolean(code));

  const [loadingInstanceInfo, setLoadingInstanceInfo] = useState(Boolean(authInfo.id_token));

  const [redirectingToInstance, setRedirectingToInstance] = useState(false);

  const [loadingCheckoutUrl, setLoadingCheckoutUrl] = useState(false);

  /* computed values */

  const planToCheckout = sessionStorage.getItem(
    SS_KEY.SELECTED_PLAN_BEFORE_SIGNIN
  );

  const loading =
    loadingUIConfig ||
    loadingAuthInfo ||
    loadingInstanceInfo ||
    loadingCheckoutUrl ||
    redirectingToInstance;

  /* effects */

  useEffect(() => {
    // scroll to top after navigation
    document.documentElement.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }, [pathname]);

  useEffect(() => {
    (async () => {
      try {
        setLoadingUIConfig(true);
        let config: IUIConfig | null = null;
        if (process.env.NODE_ENV === "development") {
          config = await new Promise<IUIConfig>((resolve) => {
            setTimeout(() => {
              resolve(fakeUiConfig as IUIConfig);
            }, 1000);
          });
        } else {
          config = await fetchUIConfig();
        }
        const loginServer = config.login_server?.[getEnv() === Environment.OFFICIAL ? 'prod' : 'dev'] || '';
        localStorage.setItem(LS_KEY.LOGIN_SERVER, loginServer);
        setUIConfig(config);
      } catch (err) {
        console.log(err);
      } finally {
        setLoadingUIConfig(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!logoutRequested) return;
    // The portal SPA cannot sign out on its own: the instance's Cognito app
    // client has neither the portal origin nor a dev origin registered as a
    // redirect target. It clears its own state and sends the user here, and the
    // root finishes the job with the client that *is* registered.
    clearSession();
    gotoOAuthPortal("logout");
  }, [logoutRequested]);

  useEffect(() => {
    (async () => {
      if (!code) return;
      try {
        /* load auth info */
        setFreshLogin(true);
        setLoadingAuthInfo(true);
        const authInfo = await fetchAuthInfoAsync(code);
        setAuthInfo(authInfo);
        storeSession(authInfo);
      } catch (err) {
        console.error(err);
        alert("Failed to fetch auth info.");
      } finally {
        setLoadingAuthInfo(false);
        // remove code from url
        navigate(pathname, { replace: true });
      }
    })();
    // eslint-disable-next-line
  }, [code]);

  useEffect(() => {
    (async function () {
      if (!authInfo.id_token) return;
      try {
        /* load instances */
        setLoadingInstanceInfo(true);
        const instances = await fetchMspInstancesAsync();
        setInstances(instances);
        const curInstance = getCurrentInstance(instances);
        const instancesWithRole = await fetchInstanceList();
        setInstancesWithRole(instancesWithRole);
        if (curInstance) {
          /* load instance details */
          const instanceDetails = await fetchMspInstanceDetailsAsync(
            curInstance.id
          );
          setCurrentInstanceDetails(instanceDetails);
          /* redirect based on instance status */
          if (pathname === "/account") return; // allow account page for debug purpose
          const { domain, installed } = instanceDetails;
          if (domain === "*") {
            navigate("/specify-domain", { replace: true }); // business plan & doamin is not set
            return;
          }
          if (!installed) {
            navigate("/checkout/success", { replace: true });
            return;
          }
          if (freshLogin) {
            if (instancesWithRole.length === 1) {
              setRedirectingToInstance(true);
              launchMspPortal(domain, "_self")
              return;
            }
          }
        } else {
          setCurrentInstanceDetails(null);
          /* load checkout url & redirect to it */
          if (planToCheckout) {
            const { type, interval, selected_at } = JSON.parse(planToCheckout) as ISelectedPlanBeforeSignin;
            const minutesEllapsed = (Date.now() - selected_at) / 1000 / 60;
            sessionStorage.removeItem(SS_KEY.SELECTED_PLAN_BEFORE_SIGNIN);
            if (minutesEllapsed <= 5) {
              setLoadingCheckoutUrl(true);
              const { url } = await checkoutAsync(type, interval);
              window.location.replace(url);
            } else {
              console.info(`selected plan in local storaged is ignored (minutes ellapsed: ${minutesEllapsed})`)
            }
          } else if (freshLogin) {
            const curInstance = instancesWithRole.length === 1 ? instancesWithRole[0] : undefined;
            if (curInstance?.domain) {
              setRedirectingToInstance(true);
              launchMspPortal(curInstance.domain, "_self")
              return;
            }
          }
        }
      } catch (err) {
        console.error(err);
        alert(err);
      } finally {
        setLoadingInstanceInfo(false);
      }
    })();
    // eslint-disable-next-line
  }, [authInfo.id_token]);

  useEffect(() => {
    /* redirect back after fresh login */
    if (loading) return;
    if (!freshLogin) return;
    const redirectPath = sessionStorage.getItem(SS_KEY.PATH_BEFORE_SIGNIN);
    if (redirectPath) {
      sessionStorage.removeItem(SS_KEY.PATH_BEFORE_SIGNIN);
      navigate(redirectPath, { replace: true });
    }
    // eslint-disable-next-line
  }, [loading]);

  /* view */

  const loaderStyle: CSSProperties = {
    height: 600,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: 24,
  };

  return (
    <UIConfigContext.Provider
      value={{ loading: loadingUIConfig, config: uiConfig }}
    >
      <AuthContext.Provider value={{ loading: loadingAuthInfo, ...authInfo }}>
        <InstanceContext.Provider
          value={{
            loading: loadingInstanceInfo,
            instances: instances,
            instancesWithRole: instancesWithRole,
            currentInstanceDetails: currentInstanceDetails,
            deleteInstanceById: (id) => {
              setInstances((arr) => arr.filter((it) => it.id !== id));
              if (id === currentInstanceDetails?.id) {
                setCurrentInstanceDetails(null);
              }
            },
            updateCurrentInstance: (newInstance: IMspInstanceDetails) => {
              setCurrentInstanceDetails(newInstance)
            }
          }}
        >
          {loading ? (
            <div style={loaderStyle}>
              <Ring size={60} />
            </div>
          ) : (
            <>
              <Header theme={pathname === "/" ? "dark" : "light"} />
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/plans" element={<Plans />} />
                {
                  (
                    window.location.host.includes("localhost") ||
                    window.location.host.includes("dd.firewalla")
                  ) && <Route path="/account" element={<Account />} />
                }
                <Route path="/specify-domain" element={<SpecifyDomain />} />
                <Route path="/checkout/success" element={<CheckoutSuccess />} />
                <Route path="/crystal" element={<Crystal />} />
                <Route path="/crystal/success" element={<CrystalSuccess />} />
                <Route path="/crystal-licenses" element={<CrystalLicenses />} />
              </Routes>
              {["/", "/plans"].includes(pathname) && <Footer />}
            </>
          )}
        </InstanceContext.Provider>
      </AuthContext.Provider>
    </UIConfigContext.Provider>
  );
};

export default App;
